import React from 'react';
import { V5Result, WinCondition, Role } from '../../types';

interface Props {
  result: V5Result;
}

export const V5ResultDisplay: React.FC<Props> = ({ result }) => {
  const { team_a, team_b, analysis } = result;

  return (
    <div className="space-y-6">
      {/* Match Narrative */}
      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-6">
        <h3 className="text-xl font-bold text-purple-900 dark:text-purple-100 mb-2">
          📖 {analysis.narrative.title}
        </h3>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {analysis.narrative.description}
        </p>

        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
              Team A Paths to Victory:
            </h4>
            <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
              {analysis.narrative.teamAStrengths.map((strength, i) => (
                <li key={i}>• {strength}</li>
              ))}
            </ul>
          </div>

          <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-4">
            <h4 className="font-semibold text-red-900 dark:text-red-100 mb-2">
              Team B Paths to Victory:
            </h4>
            <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
              {analysis.narrative.teamBStrengths.map((strength, i) => (
                <li key={i}>• {strength}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-4 p-3 bg-purple-100 dark:bg-purple-900/30 rounded">
          <p className="text-sm text-purple-900 dark:text-purple-100">
            <strong>Prediction:</strong> {analysis.narrative.prediction}
          </p>
        </div>

        <div className="mt-3 p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded">
          <p className="text-sm text-indigo-900 dark:text-indigo-100">
            <strong>Tactical Breakdown:</strong> {analysis.narrative.tacticalBreakdown}
          </p>
        </div>
      </div>

      {/* Teams */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-700 rounded-lg p-6">
          <h3 className="text-xl font-bold text-blue-900 dark:text-blue-100 mb-4">
            Team A
          </h3>
          <div className="space-y-2">
            {team_a.map((player, index) => (
              <div
                key={player.id}
                className="flex justify-between items-center bg-white dark:bg-gray-800 p-3 rounded"
              >
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {index + 1}. {player.name}
                </span>
                <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                  {player.overall_rating?.toFixed(1)}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-4 p-3 bg-blue-100 dark:bg-blue-900/30 rounded">
            <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2 text-sm">
              Win Conditions ({analysis.competitiveTension.teamAWinConditions.length}):
            </h4>
            <div className="space-y-1">
              {analysis.competitiveTension.teamAWinConditions.map((wc, i) => (
                <div key={i} className="text-xs text-blue-800 dark:text-blue-200">
                  <strong>{formatWinCondition(wc.condition)}:</strong> {wc.description}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700 rounded-lg p-6">
          <h3 className="text-xl font-bold text-red-900 dark:text-red-100 mb-4">
            Team B
          </h3>
          <div className="space-y-2">
            {team_b.map((player, index) => (
              <div
                key={player.id}
                className="flex justify-between items-center bg-white dark:bg-gray-800 p-3 rounded"
              >
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {index + 1}. {player.name}
                </span>
                <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                  {player.overall_rating?.toFixed(1)}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-4 p-3 bg-red-100 dark:bg-red-900/30 rounded">
            <h4 className="font-semibold text-red-900 dark:text-red-100 mb-2 text-sm">
              Win Conditions ({analysis.competitiveTension.teamBWinConditions.length}):
            </h4>
            <div className="space-y-1">
              {analysis.competitiveTension.teamBWinConditions.map((wc, i) => (
                <div key={i} className="text-xs text-red-800 dark:text-red-200">
                  <strong>{formatWinCondition(wc.condition)}:</strong> {wc.description}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Analysis Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <AnalysisCard
          title="Role Balance"
          score={analysis.roleBalance.score}
          quality={analysis.roleBalance.distribution}
          icon="🎭"
          color="purple"
        />
        <AnalysisCard
          title="Weakness Distribution"
          score={analysis.weaknessDistribution.score}
          quality={analysis.weaknessDistribution.quality}
          icon="⚖️"
          color="blue"
        />
        <AnalysisCard
          title="Competitive Tension"
          score={analysis.competitiveTension.score}
          quality={analysis.competitiveTension.quality}
          icon="🎯"
          color="green"
        />
        <AnalysisCard
          title="Variety"
          score={analysis.variety.score}
          quality={analysis.variety.quality}
          icon="🔄"
          color="indigo"
        />
      </div>

      {/* Overall Score */}
      <div className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg p-6 text-center">
        <h3 className="text-2xl font-bold mb-2">
          Overall Score: {analysis.overallScore.toFixed(1)}/10
        </h3>
        <p className="text-purple-100">
          Generated in {result.generation_time_ms}ms ({result.iterations.toLocaleString()} iterations)
        </p>
      </div>
    </div>
  );
};

interface AnalysisCardProps {
  title: string;
  score: number;
  quality: string;
  icon: string;
  color: 'purple' | 'blue' | 'green' | 'indigo';
}

const AnalysisCard: React.FC<AnalysisCardProps> = ({ title, score, quality, icon, color }) => {
  const colorClasses = {
    purple: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800 text-purple-900 dark:text-purple-100',
    blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-100',
    green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-900 dark:text-green-100',
    indigo: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800 text-indigo-900 dark:text-indigo-100'
  };

  return (
    <div className={`border rounded-lg p-4 ${colorClasses[color]}`}>
      <div className="text-3xl mb-2">{icon}</div>
      <h4 className="font-semibold mb-1 text-sm">{title}</h4>
      <div className="text-2xl font-bold">{score.toFixed(1)}/10</div>
      <div className="text-xs mt-1 opacity-75">{quality}</div>
    </div>
  );
};

function formatWinCondition(condition: WinCondition): string {
  return condition.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}
